
import React, { useEffect, useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Button 
} from '@/components/ui/button';
import { 
  Loader2, 
  CreditCard, 
  Calendar, 
  User, 
  Bike, 
  Receipt, 
  AlertTriangle,
  X,
  Play,
  Pause,
  CreditCardIcon,
  Pencil,
  Trash2
} from 'lucide-react';
import { SubscriptionWithRelations, mapSubscriptionFromDb } from '@/services/types/subscriptionTypes';
import { SubscriptionStatusBadge } from './SubscriptionStatusBadge';
import { formatCurrency } from '@/utils/dateUtils';
import { fetchSubscriptionDetails } from '@/pages/admin/rentals/services/subscriptionDetails';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CreateAuthPaymentDialog } from './CreateAuthPaymentDialog';
import { EditSubscriptionForm } from '@/pages/admin/rentals/components/tabs/EditSubscriptionForm';
import { ScrollArea } from '@/components/ui/scroll-area';

interface SubscriptionDetailsDialogProps {
  subscriptionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusChange: (id: string, status: 'active' | 'paused' | 'cancelled') => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onEdit: () => void;
  isEditMode: boolean;
  onCloseEditMode: () => void;
  loading: boolean;
}

export function SubscriptionDetailsDialog({
  subscriptionId,
  open,
  onOpenChange,
  onStatusChange,
  onDelete,
  onEdit,
  isEditMode,
  onCloseEditMode,
  loading
}: SubscriptionDetailsDialogProps) {
  const [subscription, setSubscription] = useState<SubscriptionWithRelations | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  
  // Загрузка деталей подписки
  useEffect(() => {
    if (open && subscriptionId) {
      loadSubscriptionDetails();
    } else {
      // Сбросить состояние при закрытии диалога
      setSubscription(null);
      setError(null);
    }
  }, [open, subscriptionId]);
  
  const loadSubscriptionDetails = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await fetchSubscriptionDetails(subscriptionId);
      if (error) {
        setError(`Ошибка при загрузке деталей подписки: ${error}`);
      } else {
        setSubscription(data);
      }
    } catch (err: any) {
      setError(`Непредвиденная ошибка: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleStatusChange = async (status: 'active' | 'paused' | 'cancelled') => {
    if (subscriptionId) {
      await onStatusChange(subscriptionId, status);
      loadSubscriptionDetails();
    }
  };
  
  const handleDelete = async () => {
    if (subscriptionId) {
      await onDelete(subscriptionId);
      onOpenChange(false);
    }
  };
  
  // Проверка, есть ли авторизационные платежи
  const hasAuthPayment = subscription?.payments?.some(p => p.status === 'auth');
  
  // Форматирование даты для отображения
  const formatDate = (dateString?: string | Date | null) => {
    if (!dateString) return 'Не указана';
    return new Date(dateString).toLocaleDateString('ru-RU');
  };
  
  // Отображение интервала подписки в удобном формате
  const getIntervalText = (interval?: string) => {
    switch(interval) {
      case 'day': return 'Ежедневно';
      case 'week': return 'Еженедельно';
      case 'month': return 'Ежемесячно';
      case 'year': return 'Ежегодно';
      default: return 'Не указан';
    }
  };
  
  // Маскировка номера карты для отображения
  const formatCardNumber = (first6?: string, last4?: string) => {
    if (!first6 || !last4) return 'Карта не указана';
    return `${first6}******${last4}`;
  };

  // Получение идентификатора курьера (обработка возможной структуры данных)
  const getCourierId = (): string | null => {
    if (subscription?.courierId) {
      return subscription.courierId;
    }
    if (subscription?.rentals?.courier_id) {
      return subscription.rentals.courier_id;
    }
    if (subscription?.rentals?.courier?.id) {
      return subscription.rentals.courier.id;
    }
    return null;
  };
  
  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle>Информация о подписке</DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[calc(90vh-150px)] px-6">
            {isLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
              </div>
            )}
            
            {error && (
              <Alert variant="destructive" className="my-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {!isLoading && subscription && (
              <div className="space-y-4 py-4">
                {/* Основная информация о подписке */}
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Подписка #{subscription.id.substring(0, 8)}</h3>
                  <SubscriptionStatusBadge status={subscription.status} />
                </div>
                
                {/* Информация о платеже */}
                <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
                  <div className="flex justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-gray-500" />
                      <span className="font-medium">Дата начала:</span>
                    </div>
                    <span>{formatDate(subscription.startDate || subscription.start_date)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-gray-500" />
                      <span className="font-medium">Следующий платеж:</span>
                    </div>
                    <span>{formatDate(subscription.nextPaymentDate || subscription.next_payment_date)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-gray-500" />
                      <span className="font-medium">Сумма:</span>
                    </div>
                    <span>{formatCurrency(subscription.amount)}</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-gray-500" />
                      <span className="font-medium">Интервал:</span>
                    </div>
                    <span>{getIntervalText(subscription.interval)}</span>
                  </div>
                </div>
                
                {/* Информация о карте */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center flex-wrap gap-2">
                    <h4 className="font-medium flex items-center gap-2">
                      <CreditCard className="h-5 w-5 text-gray-500" />
                      Платежная карта
                    </h4>
                    
                    {!hasAuthPayment && getCourierId() && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex items-center gap-1"
                        onClick={() => setShowAuthDialog(true)}
                      >
                        <CreditCardIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Создать авторизационный платеж</span>
                        <span className="inline sm:hidden">Авторизация</span>
                      </Button>
                    )}
                  </div>
                  
                  {(subscription.cardLast4 || subscription.card_last4) ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Номер карты:</span>
                        <span className="font-medium">
                          {formatCardNumber(subscription.cardFirst6 || subscription.card_first6, subscription.cardLast4 || subscription.card_last4)}
                        </span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-500">Срок действия:</span>
                        <span className="font-medium">
                          {subscription.cardExpiryMonth || subscription.card_expiry_month}/{subscription.cardExpiryYear || subscription.card_expiry_year}
                        </span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-500">Тип карты:</span>
                        <span className="font-medium">{subscription.cardType || subscription.card_type || 'Не указан'}</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-gray-400 italic">Информация о карте не указана</div>
                  )}
                </div>
                
                {/* Связанные данные (курьер и велосипед) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(subscription.courier || (subscription.rentals && subscription.rentals.courier)) && (
                    <div className="border rounded-lg p-4 space-y-2">
                      <h4 className="font-medium flex items-center gap-2">
                        <User className="h-5 w-5 text-gray-500" />
                        Курьер
                      </h4>
                      <div className="text-sm space-y-1">
                        <p className="font-medium">
                          {subscription.courierName || 
                           (subscription.rentals?.courier?.name) || 
                           'Не указан'}
                        </p>
                        {(subscription.rentals?.courier?.email) && (
                          <p>{subscription.rentals.courier.email}</p>
                        )}
                        {(subscription.courierPhone || subscription.rentals?.courier?.phone) && (
                          <p>{subscription.courierPhone || subscription.rentals.courier.phone}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {(subscription.bike || (subscription.rentals && subscription.rentals.bikes)) && (
                    <div className="border rounded-lg p-4 space-y-2">
                      <h4 className="font-medium flex items-center gap-2">
                        <Bike className="h-5 w-5 text-gray-500" />
                        Велосипед
                      </h4>
                      <div className="text-sm space-y-1">
                        <p className="font-medium">
                          {subscription.bikeName || 
                           (subscription.rentals?.bikes?.model) || 
                           'Не указана модель'}
                        </p>
                        {(subscription.rentals?.bikes?.serial_number) && (
                          <p className="text-gray-500">
                            №{subscription.rentals.bikes.serial_number}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* История платежей */}
                {subscription.payments && subscription.payments.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3">История платежей</h4>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {subscription.payments.map((payment) => (
                        <div key={payment.id} className="flex justify-between items-center py-1 border-b last:border-b-0">
                          <span>{formatDate(payment.payment_date)}</span>
                          <div className="flex items-center gap-2">
                            <span>{formatCurrency(payment.amount)}</span>
                            <Badge variant={payment.status === 'completed' ? 'success' : 
                                  payment.status === 'auth' ? 'outline' : 'secondary'}>
                              {payment.status === 'completed' ? 'Оплачен' : 
                              payment.status === 'auth' ? 'Авторизация' : 'В обработке'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
          
          <DialogFooter className="p-6 pt-4 flex-col sm:flex-row gap-2 sm:justify-between border-t">
            <div className="flex flex-wrap gap-2">
              {/* Кнопка редактирования подписки */}
              {subscription?.status !== 'cancelled' && (
                <Button 
                  onClick={onEdit} 
                  disabled={loading}
                  variant="outline" 
                  className="gap-1"
                >
                  <Pencil className="h-4 w-4" />
                  Редактировать
                </Button>
              )}
              
              {subscription?.status !== 'active' && (
                <Button 
                  onClick={() => handleStatusChange('active')} 
                  disabled={loading}
                  variant="outline" 
                  className="gap-1"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                  Активировать
                </Button>
              )}
              
              {subscription?.status !== 'paused' && subscription?.status !== 'cancelled' && (
                <Button 
                  onClick={() => handleStatusChange('paused')} 
                  disabled={loading}
                  variant="outline" 
                  className="gap-1"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pause className="h-4 w-4" />}
                  Приостановить
                </Button>
              )}
              
              {subscription?.status !== 'cancelled' && (
                <Button 
                  onClick={() => handleStatusChange('cancelled')} 
                  disabled={loading}
                  variant="outline" 
                  className="text-destructive hover:bg-destructive/10 gap-1"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                  Отменить
                </Button>
              )}
            </div>
            
            <Button 
              onClick={() => onOpenChange(false)}
              variant="secondary"
            >
              Закрыть
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Диалог создания авторизационного платежа */}
      {subscription && getCourierId() && (
        <CreateAuthPaymentDialog
          subscriptionId={subscriptionId}
          courierId={getCourierId()!}
          open={showAuthDialog}
          onOpenChange={setShowAuthDialog}
          onSuccess={loadSubscriptionDetails}
        />
      )}
      
      {/* Диалог редактирования подписки */}
      {subscription && isEditMode && (
        <EditSubscriptionForm
          subscription={subscription}
          open={isEditMode}
          onOpenChange={(open) => {
            if (!open) onCloseEditMode();
          }}
          onSubscriptionUpdated={() => {
            loadSubscriptionDetails();
            onCloseEditMode();
          }}
        />
      )}
    </>
  );
}
